import React, { useEffect, useRef, useState } from 'react';
import { agencyInfo } from '../mock';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-32 bg-white relative overflow-hidden">
      {/* 3D Geometric accent */}
      <div className="absolute top-20 right-0 w-[400px] h-[400px] opacity-5 transform rotate-12">
        <div className="w-full h-full" style={{
          backgroundImage: 'linear-gradient(45deg, #000 25%, transparent 25%), linear-gradient(-45deg, #000 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #000 75%), linear-gradient(-45deg, transparent 75%, #000 75%)',
          backgroundSize: '40px 40px',
          backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px'
        }} />
      </div>

      {/* Floating 3D shapes */}
      <div className="absolute top-10 left-10 w-32 h-32 border-2 border-black/10 transform rotate-45 animate-float-slow" style={{ animationDelay: '0s' }} />
      <div className="absolute bottom-20 right-20 w-24 h-24 border-2 border-black/10 transform -rotate-12 animate-float-slow" style={{ animationDelay: '1s' }} />

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <div 
          className="relative transform transition-all duration-1000 ease-out"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0) perspective(1000px) rotateX(0deg)' : 'translateY(50px) perspective(1000px) rotateX(5deg)'
          }}
        >
          {/* 3D Card effect */}
          <div 
            className="relative bg-white p-12 border-l-4 border-black transition-all duration-500 hover:shadow-2xl group"
            style={{
              transformStyle: 'preserve-3d',
              transform: 'translateZ(0px)',
              boxShadow: '20px 20px 60px rgba(0,0,0,0.1), -10px -10px 40px rgba(255,255,255,0.5)'
            }}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const y = e.clientY - rect.top;
              const centerX = rect.width / 2;
              const centerY = rect.height / 2;
              const rotateX = (y - centerY) / 20;
              const rotateY = (centerX - x) / 20;
              e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(10px)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
            }}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8 text-black leading-tight">
              Building Marketing
              <br />
              Intelligence
            </h2>

            <p className="text-xl text-[#666666] leading-relaxed mb-6 font-light">
              {agencyInfo.about}
            </p>
          </div>

          {/* 3D Stats with depth */}
          <div className="grid grid-cols-3 gap-12 pt-12 mt-12 border-t border-black/10">
            {[
              { value: '340%', label: 'Avg ROAS Increase', delay: '0.1s' },
              { value: '50+', label: 'AI Systems Built', delay: '0.2s' },
              { value: '24/7', label: 'Autonomous', delay: '0.3s' }
            ].map((stat, idx) => (
              <div
                key={idx}
                className="group cursor-pointer"
                style={{
                  transform: isVisible ? 'translateY(0) translateZ(0)' : 'translateY(30px) translateZ(-50px)',
                  opacity: isVisible ? 1 : 0,
                  transition: `all 0.6s ease-out ${stat.delay}`,
                  transformStyle: 'preserve-3d'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px) translateZ(30px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) translateZ(0)';
                }}
              >
                <div className="text-5xl font-bold text-black mb-2" style={{
                  textShadow: '3px 3px 0px rgba(0,0,0,0.05), 6px 6px 0px rgba(0,0,0,0.03)'
                }}>
                  {stat.value}
                </div>
                <div className="text-sm text-[#666666] uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(45deg); }
          50% { transform: translateY(-30px) rotate(45deg); }
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default About;